import axios, { type AxiosResponse } from "axios";
import type {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  Doctor,
  ResetPasswordData,
} from "@/types/auth";

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("doctor_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
      // Token expired or invalid
      localStorage.removeItem("doctor_token");
      window.location.href = "/login";
  },
);

export class AuthService {
  private authToken: string | null = null;

  setAuthToken(token: string) {
    this.authToken = token;
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/register",
        data,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/login",
        credentials,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async verifyEmail(token: string, email?: string): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/verify-email",
        { token, email },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async resendVerification(email: string): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/resend-verification",
        { email },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/forgot-password",
        { email },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async resetPassword(data: ResetPasswordData): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/reset-password",
        data,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getProfile(): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> =
        await api.get("/auth/profile");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateProfile(data: Partial<Doctor>): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.put(
        "/auth/profile",
        data,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(
        "/auth/change-password",
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post("/auth/logout");
    } catch (error: any) {
      console.error("Logout error:", error);
    } finally {
      this.setAuthToken("");
    }
  }

  async getHealthStatus(): Promise<any> {
    try {
      const response = await api.get("/auth/health");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // WhatsApp Verification
  async sendWhatsAppCode(whatsappNumber: string): Promise<any> {
    try {
      const response = await api.post("/doctor/whatsapp/send-otp", {
        whatsapp_number: whatsappNumber,
        method: "whatsapp",
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async verifyWhatsAppCode(
    whatsappNumber: string,
    otpCode: string,
  ): Promise<any> {
    try {
      const response = await api.post("/doctor/whatsapp/verify-otp", {
        whatsapp_number: whatsappNumber,
        otp_code: otpCode,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Utility methods
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  getTokenPayload(token: string): any {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Auto-configure token if available
const token = localStorage.getItem("doctor_token");
if (token) {
  authService.setAuthToken(token);
}

export default authService;
